// Owns the planner-driven "auto-build a workflow from a plan" feature
// (the Workflow Planner modal). Two plan formats are supported:
//   - format: 'workflow' → loadFullWorkflow path (complete graph in one shot)
//   - legacy steps[]    → autoBuildWorkflow path (one step at a time, runs
//                         each as it builds so later steps can use upstream
//                         outputs)
// Plus the post-coding QA flow: handleConfirmStartQa + findEmptyRequiredFields
// + runPostCodingActions. Lifted out of WorkflowCanvas to keep that file
// focused on rendering / layout. No React hooks are used inside — this is
// purely a closure factory; the `use*` name is by convention.

const { request, minimizeArrays } = require('../lib/apiClient');
const {
  startWorkflowExecution,
  subscribeWorkflowExecution,
  generateCustomObjectFromPrompt
} = require('../lib/workflowStorage');
const { cloneTemplateNode, buildGeneratedCustomTemplate } = require('../lib/canvasUtils');
const { getStartNodeOutput } = require('../lib/nodeTypes');

// Runs `worker(item)` over every item with at most `size` in flight at once.
// Workers must not reject (handle their own errors) so one failure can't
// stall the other lanes.
function runWithConcurrency(items, size, worker) {
  var cursor = 0;
  function next() {
    if (cursor >= items.length) return Promise.resolve();
    var item = items[cursor];
    cursor += 1;
    return worker(item).then(next);
  }
  var lanes = [];
  for (var i = 0; i < size && i < items.length; i++) lanes.push(next());
  return Promise.all(lanes);
}

function useAutoBuildWorkflow(deps) {
  // refs / state mutators
  var workflowStateRef = deps.workflowStateRef;
  var setWorkflowState = deps.setWorkflowState;
  var setCodingNodeIds = deps.setCodingNodeIds;
  var setQaConfirmOpen = deps.setQaConfirmOpen;
  var setQaMissingFields = deps.setQaMissingFields;
  var setAiCodingState = deps.setAiCodingState;
  var canvasFooterRef = deps.canvasFooterRef;

  // helpers borrowed from WorkflowCanvas
  var handleFormatLayout = deps.handleFormatLayout;
  var findTemplateById = deps.findTemplateById;
  var nodeFlowLabel = deps.nodeFlowLabel;
  var autoPanToCodeCard = deps.autoPanToCodeCard;

  // values from props (already destructured at top of WorkflowCanvas)
  var objectTemplates = deps.objectTemplates;
  var onRunWorkflow = deps.onRunWorkflow;
  var isRunDisabled = deps.isRunDisabled;

  // raw props for callbacks the user/host wires up
  var props = deps.props || {};

  function loadFullWorkflow(plan, cb) {
    var typeToTemplateId = {
      'start': 'sys-start',
      'variables': 'sys-variables',
      'set-variable': 'sys-set-variable',
      'condition': 'sys-condition',
      'classifier': 'sys-classifier',
      'router': 'sys-router',
      'agent': 'sys-agent',
      'console-log': 'sys-console-log',
      'file-preview': 'sys-file-preview',
      'computer-use': 'sys-computer-use'
    };
    var typeToIcon = {
      'start': 'start',
      'variables': 'variables',
      'set-variable': 'set-variable',
      'condition': 'condition',
      'classifier': 'classifier',
      'router': 'router',
      'agent': 'agent',
      'console-log': 'console-log',
      'file-preview': 'file-preview',
      'computer-use': 'computer-use'
    };
    var typeToCategory = {
      'start': 'system',
      'variables': 'system',
      'set-variable': 'system',
      'condition': 'system',
      'classifier': 'system',
      'router': 'system',
      'agent': 'system',
      'console-log': 'system',
      'file-preview': 'system',
      'computer-use': 'system',
      'custom': 'custom'
    };
    var typeToSubtitle = {
      'start': 'Entry point of the workflow',
      'variables': 'Store typed context values',
      'set-variable': 'Update a variable at runtime',
      'condition': 'Branch based on a condition',
      'classifier': 'Classify',
      'router': 'Route by exact match',
      'agent': 'AI Agent',
      'console-log': 'Log output for debugging',
      'file-preview': 'Preview a file',
      'computer-use': 'Terminal agent',
      'custom': 'Custom node'
    };
    var typeToLabel = {
      'start': 'Start',
      'variables': 'Variables',
      'set-variable': 'Set Variable',
      'condition': 'Condition',
      'classifier': 'Classify',
      'router': 'Router',
      'agent': 'Agent',
      'console-log': 'Console Log',
      'file-preview': 'File Preview',
      'computer-use': 'Computer Use',
      'custom': 'Custom'
    };

    var defaultConditionRule = {
      mode: 'simple',
      logic: 'and',
      conditions: [{ type: 'condition', id: 'cond-default', operator: 'equals', value: '' }],
      root: {
        type: 'group', id: 'group-default', logic: 'and',
        items: [{ type: 'condition', id: 'cond-default', operator: 'equals', value: '' }]
      }
    };

    function expandNode(n, idx) {
      var nodeType = n.type || 'custom';
      var templateId = typeToTemplateId[nodeType] || null;
      var isCustom = nodeType === 'custom';
      var icon = n.icon || typeToIcon[nodeType] || 'custom';
      var category = typeToCategory[nodeType] || 'custom';
      var ts = Date.now().toString(36);

      // Check if planner specified a user-library template
      var resolvedTemplate = null;
      if (isCustom && n.templateId) {
        resolvedTemplate = findTemplateById(n.templateId);
        if (resolvedTemplate) {
          templateId = n.templateId;
        } else {
          // Template not found in library — fall back to generated ID
          templateId = 'custom-' + ts + '-' + idx;
        }
      } else if (isCustom) {
        templateId = 'custom-' + ts + '-' + idx;
      }

      var nodeId = n.id || (templateId + '-node-' + ts + '-' + idx);

      var hyperparameters = null;
      // If using a library template, ALWAYS use the template's hyperparameters (they are the source of truth)
      if (resolvedTemplate && Array.isArray(resolvedTemplate.hyperparameters) && resolvedTemplate.hyperparameters.length > 0) {
        hyperparameters = resolvedTemplate.hyperparameters.map(function(p) { return Object.assign({}, p); });
      } else if (Array.isArray(n.hyperparameters) && n.hyperparameters.length > 0) {
        hyperparameters = n.hyperparameters.map(function(p) { return Object.assign({}, p); });
      }
      // Condition nodes always need the "variable" hyperparameter for the variable chip
      if (nodeType === 'condition' && !hyperparameters) {
        hyperparameters = [{ id: 'cond-var', key: 'variable', type: 'any', default: '' }];
      }

      var paramValues = {};
      if (resolvedTemplate && hyperparameters) {
        // Start with template defaults, then overlay planner's paramValues
        hyperparameters.forEach(function(p) {
          paramValues[p.key] = p.default !== undefined ? p.default : '';
        });
        if (n.paramValues && typeof n.paramValues === 'object') {
          // Only apply values for keys that actually exist in the template's hyperparameters
          var hpKeys = {};
          hyperparameters.forEach(function(p) { hpKeys[p.key] = true; });
          Object.keys(n.paramValues).forEach(function(k) {
            if (hpKeys[k]) {
              paramValues[k] = n.paramValues[k];
            }
          });
        }
      } else if (n.paramValues && typeof n.paramValues === 'object') {
        paramValues = Object.assign({}, n.paramValues);
      } else if (hyperparameters) {
        hyperparameters.forEach(function(p) {
          paramValues[p.key] = p.default !== undefined ? p.default : '';
        });
      }

      var conditionRule;
      if (n.conditionRule && typeof n.conditionRule === 'object') {
        var cr = n.conditionRule;
        var conditions = Array.isArray(cr.conditions) ? cr.conditions.map(function(c, ci) {
          return Object.assign({ type: 'condition', id: c.id || ('cond-' + ts + '-' + ci) }, c);
        }) : defaultConditionRule.conditions;
        conditionRule = {
          mode: cr.mode || 'simple',
          logic: cr.logic || 'and',
          conditions: conditions,
          root: cr.root || {
            type: 'group', id: 'group-' + ts, logic: cr.logic || 'and',
            items: conditions
          }
        };
      } else {
        conditionRule = defaultConditionRule;
      }

      return {
        id: nodeId,
        kind: 'workflow-node',
        nodeType: 'card',
        category: category,
        title: n.title || typeToLabel[nodeType] || 'Node',
        subtitle: typeToSubtitle[nodeType] || 'Custom node',
        icon: n.icon || (resolvedTemplate && resolvedTemplate.icon ? resolvedTemplate.icon : icon),
        logoUrl: n.logoUrl || (resolvedTemplate && resolvedTemplate.logoUrl ? resolvedTemplate.logoUrl : null),
        templateId: templateId,
        code: resolvedTemplate && resolvedTemplate.code ? resolvedTemplate.code : '',
        generationPrompt: n.generationPrompt || (resolvedTemplate && resolvedTemplate.generationPrompt ? String(resolvedTemplate.generationPrompt) : ''),
        changeHistory: Array.isArray(n.changeHistory) ? n.changeHistory : [],
        label: typeToLabel[nodeType] || n.title || 'Custom',
        borderColor: '',
        options: null,
        stats: null,
        hyperparameters: hyperparameters,
        conditionRule: (nodeType === 'condition' || nodeType === 'classifier' || nodeType === 'router') ? conditionRule : defaultConditionRule,
        size: n.size || null,
        paramValues: paramValues,
        // Per-node OAuth account pin (e.g. { google: "<credId>" }). The planner
        // sets this when the user picked a default account so the built node runs
        // against that account; absent → runtime falls back to the first account.
        credentialIds: (n.credentialIds && typeof n.credentialIds === 'object') ? n.credentialIds : undefined,
        position: n.position || { left: 100 + idx * 350, top: 280 }
      };
    }

    var expandedNodes = [];
    var nodeIdMap = {};
    for (var i = 0; i < plan.nodes.length; i++) {
      var expanded = expandNode(plan.nodes[i], i);
      nodeIdMap[plan.nodes[i].id || ''] = expanded.id;
      expandedNodes.push(expanded);
    }

    var expandedConnections = [];
    var seenConnKeys = {};
    for (var ci = 0; ci < plan.connections.length; ci++) {
      var conn = plan.connections[ci];
      var fromId = nodeIdMap[conn.from] || conn.from;
      var toId = nodeIdMap[conn.to] || conn.to;
      var connKey = fromId + '>' + toId + '>' + (conn.fromAnchor || 'right');
      if (seenConnKeys[connKey]) continue; // skip duplicate connections
      seenConnKeys[connKey] = true;
      expandedConnections.push({
        id: 'plan-conn-' + Date.now().toString(36) + '-' + ci,
        from: fromId,
        to: toId,
        fromAnchor: conn.fromAnchor || 'right',
        toAnchor: conn.toAnchor || 'left',
        propPath: conn.propPath || undefined,
        noWait: conn.noWait || false,
        noData: conn.noData || false,
        branch: conn.branch || undefined
      });
    }

    // Repair disconnected non-variable nodes: find nodes with no incoming
    // connection and link them to the nearest prior node in the layout.
    var connectedTargets = {};
    for (var cti = 0; cti < expandedConnections.length; cti++) {
      connectedTargets[expandedConnections[cti].to] = true;
    }
    var nonVarNodes = expandedNodes.filter(function(n) {
      return n.icon !== 'variables' && n.icon !== 'start';
    });
    for (var ri = 0; ri < nonVarNodes.length; ri++) {
      var rNode = nonVarNodes[ri];
      if (connectedTargets[rNode.id]) continue;
      // Find the closest node to the left (by position) that is not this node
      var bestSource = null;
      var bestDist = Infinity;
      var rLeft = (rNode.position && rNode.position.left) || 0;
      var rTop = (rNode.position && rNode.position.top) || 0;
      for (var si2 = 0; si2 < expandedNodes.length; si2++) {
        var candidate = expandedNodes[si2];
        if (candidate.id === rNode.id || candidate.icon === 'variables') continue;
        var cLeft = (candidate.position && candidate.position.left) || 0;
        var cTop = (candidate.position && candidate.position.top) || 0;
        if (cLeft >= rLeft) continue; // must be to the left
        var dist = Math.abs(rLeft - cLeft) + Math.abs(rTop - cTop);
        if (dist < bestDist) { bestDist = dist; bestSource = candidate; }
      }
      if (bestSource) {
        expandedConnections.push({
          id: 'plan-conn-repair-' + Date.now().toString(36) + '-' + ri,
          from: bestSource.id,
          to: rNode.id,
          fromAnchor: 'right',
          toAnchor: 'left'
        });
        connectedTargets[rNode.id] = true;
      }
    }

    // --- Auto-layout: assign layers via topological sort, then space nodes ---
    var LAYER_X_SPACING = 350;   // horizontal gap between layers
    var NODE_VERTICAL_GAP = 200; // vertical gap between sibling nodes
    var LAYOUT_START_X = 100;
    var LAYOUT_START_Y = 250;

    var flowNodes = expandedNodes.filter(function(n) { return n.icon !== 'variables'; });

    // Build adjacency from connections
    var childrenMap = {};  // parentId -> [childId, ...]
    var parentMap = {};    // childId -> [parentId, ...]
    var nodeById = {};
    for (var fi = 0; fi < flowNodes.length; fi++) {
      var fnId = flowNodes[fi].id;
      nodeById[fnId] = flowNodes[fi];
      if (!childrenMap[fnId]) childrenMap[fnId] = [];
      if (!parentMap[fnId]) parentMap[fnId] = [];
    }
    for (var eci = 0; eci < expandedConnections.length; eci++) {
      var ec = expandedConnections[eci];
      if (nodeById[ec.from] && nodeById[ec.to]) {
        if (childrenMap[ec.from].indexOf(ec.to) === -1) childrenMap[ec.from].push(ec.to);
        if (parentMap[ec.to].indexOf(ec.from) === -1) parentMap[ec.to].push(ec.from);
      }
    }

    // Assign layers using longest-path from roots (nodes with no parents)
    var layerOf = {};
    var visited = {};
    function assignLayer(nid) {
      if (visited[nid]) return layerOf[nid] || 0;
      visited[nid] = true;
      var parents = parentMap[nid] || [];
      if (parents.length === 0) {
        layerOf[nid] = 0;
        return 0;
      }
      var maxParent = 0;
      for (var pi = 0; pi < parents.length; pi++) {
        var pl = assignLayer(parents[pi]);
        if (pl + 1 > maxParent) maxParent = pl + 1;
      }
      layerOf[nid] = maxParent;
      return maxParent;
    }
    for (var li = 0; li < flowNodes.length; li++) {
      assignLayer(flowNodes[li].id);
    }

    // Group nodes by layer
    var layers = {};
    var maxLayer = 0;
    for (var gi = 0; gi < flowNodes.length; gi++) {
      var layer = layerOf[flowNodes[gi].id] || 0;
      if (!layers[layer]) layers[layer] = [];
      layers[layer].push(flowNodes[gi]);
      if (layer > maxLayer) maxLayer = layer;
    }

    // Within each layer, sort nodes by their original top position to preserve
    // the AI's intended vertical ordering, then space them evenly
    for (var layIdx = 0; layIdx <= maxLayer; layIdx++) {
      var layerNodes = layers[layIdx];
      if (!layerNodes) continue;
      // Sort by original top position
      layerNodes.sort(function(a, b) {
        var at = (a.position && a.position.top) || 0;
        var bt = (b.position && b.position.top) || 0;
        return at - bt;
      });
      // Center the layer vertically around LAYOUT_START_Y
      var totalHeight = (layerNodes.length - 1) * NODE_VERTICAL_GAP;
      var startY = LAYOUT_START_Y - totalHeight / 2;
      for (var ni = 0; ni < layerNodes.length; ni++) {
        layerNodes[ni].position = {
          left: LAYOUT_START_X + layIdx * LAYER_X_SPACING,
          top: Math.round(startY + ni * NODE_VERTICAL_GAP)
        };
      }
    }

    // --- Fix connector anchors based on actual node positions ---
    for (var aci = 0; aci < expandedConnections.length; aci++) {
      var ac = expandedConnections[aci];
      var fromNode = nodeById[ac.from];
      var toNode = nodeById[ac.to];
      if (!fromNode || !toNode) continue;
      // Skip classifier/router anchors - those are semantic, not positional
      if (ac.fromAnchor && ac.fromAnchor.indexOf('classifier') === 0) continue;
      // Skip condition-node outputs - YES/NO anchors are semantic per toAnchor
      // (ta="left" → YES=top, NO=right). Rewriting them by relative position
      // flips the YES/NO label, e.g. a YES emitted as fa="top" becomes fa="right"
      // (which is the NO axis) just because the target sits to the right.
      if (fromNode.type === 'condition') continue;
      var fLeft = (fromNode.position && fromNode.position.left) || 0;
      var fTop = (fromNode.position && fromNode.position.top) || 0;
      var tLeft = (toNode.position && toNode.position.left) || 0;
      var tTop = (toNode.position && toNode.position.top) || 0;
      var dx = tLeft - fLeft;
      var dy = tTop - fTop;
      // Determine best anchors based on relative position
      if (Math.abs(dx) >= Math.abs(dy)) {
        // Primarily horizontal
        if (dx >= 0) {
          ac.fromAnchor = 'right';
          ac.toAnchor = 'left';
        } else {
          ac.fromAnchor = 'left';
          ac.toAnchor = 'right';
        }
      } else {
        // Primarily vertical
        if (dy >= 0) {
          ac.fromAnchor = 'bottom';
          ac.toAnchor = 'top';
        } else {
          ac.fromAnchor = 'top';
          ac.toAnchor = 'bottom';
        }
      }
    }

    // Place nodes on canvas first (code will be generated per-node)
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        nodes: expandedNodes,
        connections: expandedConnections
      });
    });

    // Find custom nodes that need code generation (have generationPrompt but no code)
    var codePending = [];
    for (var pi2 = 0; pi2 < expandedNodes.length; pi2++) {
      var pn = expandedNodes[pi2];
      if ((pn.icon === 'custom' || pn.icon === 'agent') && pn.generationPrompt && !pn.code) {
        codePending.push({ index: pi2, node: pn });
      }
    }

    if (codePending.length === 0) {
      // No code generation needed — all steps done
      if (cb.onStepStart) cb.onStepStart(0, 'building');
      for (var si = 0; si < plan.nodes.length; si++) {
        if (cb.onStepDone) cb.onStepDone(si);
      }
      if (cb.onDone) cb.onDone();
      return;
    }

    // Build workflow context for code generation
    var buildSourceVariables = {};
    expandedNodes.forEach(function(n) {
      if (!n || n.icon !== 'variables') return;
      var name = String(n.title || '').trim();
      if (!name) return;
      var pv = n.paramValues || {};
      buildSourceVariables[name] = { dataType: String(pv.dataType || 'string'), value: pv.value };
    });

    var buildStartNode = null;
    for (var bsi = 0; bsi < expandedNodes.length; bsi++) {
      if (expandedNodes[bsi] && expandedNodes[bsi].icon === 'start') { buildStartNode = expandedNodes[bsi]; break; }
    }
    var buildStartParams = null;
    if (buildStartNode && Array.isArray(buildStartNode.hyperparameters) && buildStartNode.hyperparameters.length) {
      buildStartParams = {};
      buildStartNode.hyperparameters.forEach(function(p) {
        if (!p || !p.key) return;
        buildStartParams[p.key] = (buildStartNode.paramValues && buildStartNode.paramValues[p.key] !== undefined)
          ? buildStartNode.paramValues[p.key]
          : (p.default !== undefined ? p.default : '');
      });
    }

    if (cb.onStepStart) cb.onStepStart(0, 'building');

    var failedNodeTitles = [];

    function generateForNode(pending) {
      var nodeIdx = pending.index;
      var node = pending.node;
      if (cb.onStepStart) cb.onStepStart(nodeIdx, 'coding');
      // Only the node being generated right now shows its spinner — the
      // rest sit as plain stubs until their turn.
      setCodingNodeIds(function(prev) {
        var next = Object.assign({}, prev);
        next[node.id] = true;
        return next;
      });

      var genPrompt = node.generationPrompt;
      var genOptions = {};
      if (node.icon === 'agent') {
        genOptions.agentMode = true;
      }
      // Pass workflow context so the code generator has full information
      var genContext = {};
      if (Object.keys(buildSourceVariables).length > 0) {
        genContext.sourceVariables = buildSourceVariables;
      }
      if (buildStartParams) {
        genContext.startParams = buildStartParams;
      }
      if (Object.keys(genContext).length > 0) {
        genOptions.context = genContext;
      }

      // One retry after a short pause — transient 429/500/network errors are
      // the usual reason a node used to end the build with no code at all.
      function attemptGenerate(retriesLeft) {
        return generateCustomObjectFromPrompt(genPrompt, genOptions).catch(function(err) {
          if (retriesLeft > 0) {
            console.warn('[Build] Code generation failed for node, retrying:', node.title, err);
            return new Promise(function(resolve) { setTimeout(resolve, 1500); }).then(function() {
              return attemptGenerate(retriesLeft - 1);
            });
          }
          throw err;
        });
      }

      return attemptGenerate(1).then(function(generated) {
        // Update node with generated code
        setWorkflowState(function(prev) {
          var updatedNodes = prev.nodes.map(function(n) {
            if (n.id === node.id) {
              var updates = { code: generated.code };
              // Use generated hyperparameters if node didn't have any from planner
              if ((!n.hyperparameters || n.hyperparameters.length === 0) && Array.isArray(generated.hyperparameters) && generated.hyperparameters.length > 0) {
                updates.hyperparameters = generated.hyperparameters;
                var newParamValues = Object.assign({}, n.paramValues || {});
                generated.hyperparameters.forEach(function(hp) {
                  if (!(hp.key in newParamValues) && hp.default !== undefined) {
                    newParamValues[hp.key] = hp.default;
                  }
                });
                updates.paramValues = newParamValues;
              }
              return Object.assign({}, n, updates);
            }
            return n;
          });
          return Object.assign({}, prev, { nodes: updatedNodes });
        });
      }).catch(function(err) {
        console.error('[Build] Code generation failed for node:', node.title, err);
        failedNodeTitles.push(node.title || 'Untitled node');
      }).then(function() {
        // Success or failure: clear the spinner and advance the step counter.
        setCodingNodeIds(function(prev) {
          var next = Object.assign({}, prev);
          delete next[node.id];
          return next;
        });
        if (cb.onStepDone) cb.onStepDone(nodeIdx);
      });
    }

    // Strictly one node at a time — parallel codegen overloaded the API and
    // left nodes stuck on "Coding."; each node also benefits from the state
    // committed by the ones before it.
    runWithConcurrency(codePending, 1, generateForNode).then(function() {
      setCodingNodeIds({});
      if (failedNodeTitles.length > 0) {
        // Don't report "Done!" — tell the user exactly which nodes have no
        // code so they can regenerate them instead of discovering stubs later.
        try { handleFormatLayout('default'); } catch (e) {}
        if (cb.onError) {
          cb.onError(new Error(
            'Code generation failed for: ' + failedNodeTitles.join(', ') +
            '. These nodes were placed without code — open each one to regenerate it, or send a message here to rebuild them.'
          ));
        }
        return;
      }
      for (var di = 0; di < plan.nodes.length; di++) {
        if (cb.onStepDone) cb.onStepDone(di);
      }
      if (cb.onDone) cb.onDone();
      // After coding finishes: format the layout, switch the silent-error
      // checker on, then auto-run the workflow. Small delays let React commit
      // the freshly-generated code and layout state before each next step.
      runPostCodingActions();
    });
  }

  // Triggered once all auto-coded nodes finish generating. Always formats
  // the layout (cheap, undoable), then asks the user before kicking off the
  // QA pass — toggling Check Silent Errors on and running a workflow are
  // both side effects (credits, network) the user should opt into.
  function runPostCodingActions() {
    try { handleFormatLayout('default'); } catch (e) {}
    if (typeof onRunWorkflow === 'function' && !isRunDisabled) {
      setQaConfirmOpen(true);
    }
  }

  // Collect every empty + required field across the workflow so the QA flow
  // can block the run until the user fills them in. "Required + empty" here
  // means: the field has no usable value AND no fallback default — there is
  // genuinely nothing for the runtime to read. Three sources:
  //   1. Variable nodes (icon="variables") with no pv.value (and not retain).
  //   2. Start node hyperparameters whose paramValues[key] is empty AND whose
  //      hp.default is also empty.
  //   3. Custom-node hyperparameters whose paramValues[key] is empty AND whose
  //      hp.default is also empty.
  // Returns an array of { nodeId, nodeTitle, fieldLabel, kind } so the dialog
  // can render a useful list. Empty list = nothing to fill in, run can proceed.
  // NOTE: reads from workflowStateRef.current rather than a captured workflowState
  // value (the original code in WorkflowCanvas read workflowState directly).
  // The ref is updated on every render, so they hold the same value at the
  // moment this function is called — the substitution is behavior-preserving.
  function findEmptyRequiredFields() {
    var snapshot = workflowStateRef.current || {};
    var nodesList = snapshot.nodes || [];
    var missing = [];
    function isBlank(v) {
      if (v === null || v === undefined) return true;
      if (typeof v === 'string') return v.trim() === '';
      return false;
    }
    // Only Start hyperparameters and custom-node hyperparameters. Variables
    // are intentionally excluded — many of them are runtime-written
    // (set-variable populates them mid-flow) and asking the user to pre-fill
    // those is wrong.
    nodesList.forEach(function(node) {
      if (!node) return;
      var icon = String(node.icon || node.type || '').toLowerCase();
      if (icon === 'variables') return;
      var pv = node.paramValues || {};
      var hps = Array.isArray(node.hyperparameters) ? node.hyperparameters : [];
      if (hps.length === 0) return;
      hps.forEach(function(hp) {
        if (!hp || !hp.key) return;
        var current = pv[hp.key];
        if (!isBlank(current)) return;
        if (!isBlank(hp.default)) return;
        missing.push({
          nodeId: node.id,
          nodeTitle: node.title || (icon === 'start' ? 'Start' : 'Node'),
          fieldLabel: hp.key,
          kind: icon === 'start' ? 'start-param' : 'node-param',
          paramKey: hp.key,
          paramType: hp.type || 'string',
          currentValue: current == null ? '' : current
        });
      });
    });
    return missing;
  }

  // User said "yes, start QA" in the confirmation dialog: first scan for
  // empty required fields. If any are missing, swap to the missing-fields
  // dialog instead of running — the workflow would just fail on the first
  // node otherwise. Otherwise turn the checker on and click the actual play
  // <button> in CanvasFooter (same DOM path as a real user click).
  function handleConfirmStartQa() {
    var missing = findEmptyRequiredFields();
    if (missing.length > 0) {
      setQaConfirmOpen(false);
      setQaMissingFields(missing);
      return;
    }
    setQaConfirmOpen(false);
    if (typeof props.onCheckSilentErrorChange === 'function') {
      try { props.onCheckSilentErrorChange(true); } catch (e) {}
    }
    setTimeout(function() {
      if (canvasFooterRef.current && typeof canvasFooterRef.current.clickRun === 'function') {
        try { canvasFooterRef.current.clickRun(); } catch (e) {}
      }
    }, 250);
  }

  function autoBuildWorkflow(plan, callbacks) {
    var cb = callbacks || {};

    if (plan && plan.format === 'workflow' && Array.isArray(plan.nodes)) {
      loadFullWorkflow(plan, cb);
      return;
    }

    var steps = (plan && Array.isArray(plan.steps)) ? plan.steps : [];
    if (steps.length === 0) {
      if (cb.onError) cb.onError(new Error('Plan has no steps'));
      return;
    }

    var builtNodes = [];
    var builtConnections = [];
    var prevNodeId = null;
    var prevNodeOutput = undefined;
    var outputByNodeId = {};
    var stepNodeIdByRef = {};
    var xOffset = 100;
    var yCenter = 280;

    function normalizePlannerStepRef(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    }

    function getPlannerStepRef(step, index) {
      var explicitId = normalizePlannerStepRef(step && step.id);
      if (explicitId) return explicitId;
      return 'step_' + index;
    }

    function registerPlannerStepNode(step, index, nodeId) {
      var ref = getPlannerStepRef(step, index);
      if (!ref || !nodeId) return;
      stepNodeIdByRef[ref] = nodeId;
    }

    function findBuiltNodeById(nodeId) {
      for (var i = 0; i < builtNodes.length; i += 1) {
        if (builtNodes[i] && String(builtNodes[i].id) === String(nodeId)) return builtNodes[i];
      }
      return null;
    }

    function resolvePlannerSourceNodeId(step) {
      var ref = normalizePlannerStepRef(step && step.connectFrom);
      if (ref && stepNodeIdByRef[ref]) return stepNodeIdByRef[ref];
      return prevNodeId;
    }

    function resolvePlannerSourceAnchor(step, sourceNodeId) {
      var sourceNode = findBuiltNodeById(sourceNodeId);
      var branch = String(step && step.connectFromBranch || '').trim().toLowerCase();
      if (sourceNode && sourceNode.icon === 'condition') {
        if (branch === 'yes') return 'top';
        return 'bottom';
      }
      return 'right';
    }

    function createPlannedConnection(step, nodeId) {
      var sourceNodeId = resolvePlannerSourceNodeId(step);
      if (!sourceNodeId) return null;
      return {
        id: 'plan-conn-' + nodeId,
        from: sourceNodeId,
        to: nodeId,
        fromAnchor: resolvePlannerSourceAnchor(step, sourceNodeId),
        toAnchor: 'left'
      };
    }

    function setSnapshot() {
      var snapNodes = builtNodes.slice();
      var snapConns = builtConnections.slice();
      setWorkflowState(function(prev) {
        return Object.assign({}, prev, { nodes: snapNodes, connections: snapConns });
      });
    }

    function runWorkflowSnapshot(onRunStart, useLatestWorkflowState) {
      if (onRunStart) onRunStart();
      var snapNodes = builtNodes.slice();
      var snapConns = builtConnections.slice();
      if (useLatestWorkflowState) {
        var currentState = workflowStateRef.current || {};
        if (Array.isArray(currentState.nodes)) snapNodes = currentState.nodes.slice();
        if (Array.isArray(currentState.connections)) snapConns = currentState.connections.slice();
        builtNodes = snapNodes.slice();
        builtConnections = snapConns.slice();
      }
      var snapshot = Object.assign({}, workflowStateRef.current || {}, {
        nodes: snapNodes,
        connections: snapConns
      });
      var onRunWithSnapshot = props.onRunWithSnapshot;
      return (onRunWithSnapshot ? onRunWithSnapshot(snapshot) : startWorkflowExecution(snapshot)).then(function(result) {
        var executionId = result && result.executionId ? result.executionId : null;
        if (!executionId) return { nodeOutputById: {} };
        return new Promise(function(resolve) {
          var outputs = {};
          var runFailed = false;
          var runError = '';
          var firstFailedNodeId = null;
          var settled = false;
          function finish(failed, error) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            closeStream();
            resolve({ nodeOutputById: outputs, failed: !!failed, error: error || '', failedNodeId: firstFailedNodeId });
          }
          var closeStream = subscribeWorkflowExecution(executionId, {
            onEvent: function(event) {
              if (event.type === 'node.completed' && event.nodeId) {
                outputs[event.nodeId] = event.output !== undefined ? event.output : null;
              }
              if (event.type === 'node.failed' && event.nodeId) {
                runFailed = true;
                firstFailedNodeId = firstFailedNodeId || event.nodeId;
                runError = runError || (event.error ? String(event.error) : 'Node execution failed.');
                outputs[event.nodeId] = null;
              }
              if (event.type === 'execution.failed') {
                runFailed = true;
                runError = runError || (event.error ? String(event.error) : 'Execution failed.');
              }
              if (event.type === 'node.fix.applied' && event.nodeId && typeof event.fixedCode === 'string') {
                builtNodes = builtNodes.map(function(n) {
                  if (!n || n.id !== event.nodeId) return n;
                  return Object.assign({}, n, { code: event.fixedCode });
                });
              }
              if (event.type === 'execution.completed' || event.type === 'execution.failed') {
                finish(runFailed, runError);
              }
            },
            onError: function() { finish(true, 'Execution stream disconnected.'); }
          });
          var timer = setTimeout(function() { finish(true, 'Execution timed out.'); }, 90000);
        });
      }).catch(function() { return { nodeOutputById: {}, failed: true, error: 'Could not start execution.' }; });
    }

    function buildGenerateContext(sourceNodeId) {
      var sourceVariables = {};
      builtNodes.forEach(function(node) {
        if (!node || node.icon !== 'variables') return;
        var name = String(node.title || '').trim();
        if (!name) return;
        var pv = node.paramValues || {};
        sourceVariables[name] = { dataType: String(pv.dataType || 'string'), value: pv.value };
      });
      var startNode = null;
      for (var i = 0; i < builtNodes.length; i++) {
        if (builtNodes[i] && builtNodes[i].icon === 'start') { startNode = builtNodes[i]; break; }
      }
      var startParams = null;
      if (startNode && Array.isArray(startNode.hyperparameters) && startNode.hyperparameters.length) {
        startParams = {};
        startNode.hyperparameters.forEach(function(p) {
          if (!p || !p.key) return;
          startParams[p.key] = (startNode.paramValues && startNode.paramValues[p.key] !== undefined)
            ? startNode.paramValues[p.key]
            : (p.default !== undefined ? p.default : '');
        });
      }
      var builtConnByTarget = {};
      builtConnections.forEach(function(conn) {
        if (conn && conn.to) builtConnByTarget[String(conn.to)] = conn;
      });
      var nodeFlow = builtNodes
        .filter(function(n) { return n && n.icon !== 'variables'; })
        .map(function(n) {
          var label = nodeFlowLabel(n, builtConnByTarget[n.id] || null);
          return label !== null ? label : null;
        })
        .filter(function(l) { return l !== null; });
      var effectiveSourceNodeId = sourceNodeId || prevNodeId;
      var sourceNode = findBuiltNodeById(effectiveSourceNodeId);
      var prevNodeTitle = sourceNode ? (sourceNode.title || '') : '';
      var rawSourceOutput = effectiveSourceNodeId
        ? (outputByNodeId[effectiveSourceNodeId] !== undefined
            ? outputByNodeId[effectiveSourceNodeId]
            : (sourceNode && sourceNode.icon === 'start' ? getStartNodeOutput(sourceNode) : undefined))
        : prevNodeOutput;
      // Trim big arrays/base64 so the generate context stays well under the request
      // cap (413) — codegen only needs the shape + a sample (a no-op on non-objects).
      var effectiveSourceOutput = minimizeArrays(rawSourceOutput);
      var directlyConnectedToStart = !!(sourceNode && sourceNode.icon === 'start');
      return {
        sourceNodeId: effectiveSourceNodeId,
        sourceNodeTitle: prevNodeTitle,
        nodeFlow: nodeFlow,
        sourceOutput: effectiveSourceOutput,
        sourceVariables: sourceVariables,
        startParams: startParams,
        directlyConnectedToStart: directlyConnectedToStart
      };
    }

    function runStep(stepIndex, captureId, onContinue, useLatestWorkflowState) {
      if (cb.onStepStart) cb.onStepStart(stepIndex, 'running');
      runWorkflowSnapshot(null, !!useLatestWorkflowState).then(function(run) {
        if (run.failed) {
          if (cb.onRunError) {
            cb.onRunError(run.error || 'Execution failed.', function resume() {
              runStep(stepIndex, captureId, onContinue, true);
            }, run.failedNodeId || captureId);
          } else {
            if (cb.onStepDone) cb.onStepDone(stepIndex);
            onContinue(undefined);
          }
          return;
        }
        var output = (captureId && run.nodeOutputById[captureId] !== undefined)
          ? run.nodeOutputById[captureId]
          : undefined;
        if (captureId && output !== undefined) outputByNodeId[captureId] = output;
        if (cb.onStepDone) cb.onStepDone(stepIndex);
        onContinue(output);
      });
    }

    function processStep(index) {
      if (index >= steps.length) {
        if (cb.onDone) cb.onDone();
        return;
      }

      if (cb.onStepStart) cb.onStepStart(index, 'building');

      var step = steps[index];

      if (step.type === 'start') {
        var startTemplate = null;
        for (var j = 0; j < objectTemplates.length; j++) {
          if (objectTemplates[j].id === 'sys-start') { startTemplate = objectTemplates[j]; break; }
        }
        if (!startTemplate) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }

        var tplToUse = startTemplate;
        if (Array.isArray(step.params) && step.params.length > 0) {
          var validParams = step.params.filter(function(p) { return p && p.key; }).map(function(p, pi) {
            return Object.assign({ id: 'planner-p-' + pi + '-' + p.key }, p);
          });
          if (validParams.length > 0) tplToUse = Object.assign({}, startTemplate, { hyperparameters: validParams });
        }

        var startId = 'sys-start-node-' + Date.now().toString(36) + '-' + index;
        builtNodes = builtNodes.concat([cloneTemplateNode(tplToUse, startId, { left: xOffset, top: yCenter })]);
        registerPlannerStepNode(step, index, startId);
        setSnapshot();
        prevNodeId = startId;
        xOffset += 350;

        var capturedStartId = startId;
        runStep(index, capturedStartId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'variable') {
        var varTemplate = null;
        for (var vi = 0; vi < objectTemplates.length; vi++) {
          if (objectTemplates[vi].id === 'sys-variables') { varTemplate = objectTemplates[vi]; break; }
        }
        if (!varTemplate) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }

        var varId = 'sys-variables-' + Date.now().toString(36) + '-' + index;
        var varNodeBase = cloneTemplateNode(
          Object.assign({}, varTemplate, { title: step.label || step.key || 'Variable' }),
          varId, { left: 0, top: 0 }
        );
        var varNode = Object.assign({}, varNodeBase, {
          paramValues: {
            dataType: step.dataType || 'string',
            value: step.defaultValue || '',
            retain: String(step.mode || '').toLowerCase() === 'static' ? 'true' : 'false'
          }
        });
        builtNodes = builtNodes.concat([varNode]);
        registerPlannerStepNode(step, index, varId);
        setSnapshot();
        if (cb.onStepDone) cb.onStepDone(index);
        processStep(index + 1);
        return;
      }

      if (step.type === 'condition') {
        var condTpl = null;
        for (var ci = 0; ci < objectTemplates.length; ci++) {
          if (objectTemplates[ci].id === 'sys-condition') { condTpl = objectTemplates[ci]; break; }
        }
        if (!condTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var condId = 'sys-condition-' + Date.now().toString(36) + '-' + index;
        var condBase = cloneTemplateNode(condTpl, condId, { left: xOffset, top: yCenter });
        var condConditions = Array.isArray(step.conditions) && step.conditions.length
          ? step.conditions.map(function(c, ci2) { return Object.assign({ id: 'cond-' + ci2 }, c); })
          : [{ id: 'cond-0', operator: 'equals', value: '' }];
        var condNode = Object.assign({}, condBase, {
          paramValues: Object.assign({}, condBase.paramValues, { variable: step.conditionVariable || '' }),
          conditionRule: { mode: step.conditionMode || 'simple', logic: step.conditionLogic || 'and', conditions: condConditions }
        });
        builtNodes = builtNodes.concat([condNode]);
        registerPlannerStepNode(step, index, condId);
        var condConn = createPlannedConnection(step, condId);
        if (condConn) builtConnections = builtConnections.concat([condConn]);
        setSnapshot();
        prevNodeId = condId;
        xOffset += 350;
        var capturedCondId = condId;
        runStep(index, capturedCondId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'set-variable') {
        var setVarTpl = null;
        for (var svi = 0; svi < objectTemplates.length; svi++) {
          if (objectTemplates[svi].id === 'sys-set-variable') { setVarTpl = objectTemplates[svi]; break; }
        }
        if (!setVarTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var setVarId = 'sys-set-variable-' + Date.now().toString(36) + '-' + index;
        var setVarBase = cloneTemplateNode(setVarTpl, setVarId, { left: xOffset, top: yCenter });

        var builtVarNodes = builtNodes.filter(function(n) { return n && n.icon === 'variables'; });
        var resolvedTarget = step.targetVariable || '';
        if (resolvedTarget && builtVarNodes.length) {
          var exactMatch = builtVarNodes.some(function(n) { return String(n.title || '') === resolvedTarget; });
          if (!exactMatch) {
            var lower = resolvedTarget.toLowerCase();
            var ciMatch = builtVarNodes.find(function(n) { return String(n.title || '').toLowerCase() === lower; });
            if (ciMatch) resolvedTarget = ciMatch.title;
          }
        }
        if (!resolvedTarget && builtVarNodes.length) {
          resolvedTarget = String(builtVarNodes[builtVarNodes.length - 1].title || '');
        }

        var setVarNode = Object.assign({}, setVarBase, {
          paramValues: { variable: resolvedTarget, source: step.fromInput ? 'true' : 'false', value: step.value || '' }
        });
        builtNodes = builtNodes.concat([setVarNode]);
        registerPlannerStepNode(step, index, setVarId);
        var conn = createPlannedConnection(step, setVarId);
        if (conn) {
          if (step.fromInput && step.inputPath) conn.propPath = String(step.inputPath).trim();
          builtConnections = builtConnections.concat([conn]);
        }
        setSnapshot();
        prevNodeId = setVarId;
        xOffset += 350;
        var capturedSetVarId = setVarId;
        runStep(index, capturedSetVarId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'console-log') {
        var logTpl = null;
        for (var li = 0; li < objectTemplates.length; li++) {
          if (objectTemplates[li].id === 'sys-console-log') { logTpl = objectTemplates[li]; break; }
        }
        if (!logTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var logId = 'sys-console-log-' + Date.now().toString(36) + '-' + index;
        var logNode = cloneTemplateNode(logTpl, logId, { left: xOffset, top: yCenter });
        builtNodes = builtNodes.concat([logNode]);
        registerPlannerStepNode(step, index, logId);
        var logConn = createPlannedConnection(step, logId);
        if (logConn) builtConnections = builtConnections.concat([logConn]);
        setSnapshot();
        prevNodeId = logId;
        xOffset += 350;
        var capturedLogId = logId;
        runStep(index, capturedLogId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'agent') {
        var agentTpl = null;
        for (var agi = 0; agi < objectTemplates.length; agi++) {
          if (objectTemplates[agi].id === 'sys-agent') { agentTpl = objectTemplates[agi]; break; }
        }
        if (!agentTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var agentId = 'sys-agent-' + Date.now().toString(36) + '-' + index;
        var agentBase = cloneTemplateNode(agentTpl, agentId, { left: xOffset, top: yCenter });
        var agentNode = Object.assign({}, agentBase, {
          title: step.description ? step.description.substring(0, 40) : 'Agent',
          paramValues: {
            system: step.system || 'You are a helpful AI assistant.',
            prompt: step.prompt || '',
            instruction: step.instruction || '',
            temperature: step.temperature !== undefined ? step.temperature : 0.7,
            maxTokens: step.maxTokens || 8000
          }
        });
        builtNodes = builtNodes.concat([agentNode]);
        registerPlannerStepNode(step, index, agentId);
        var agentConn = createPlannedConnection(step, agentId);
        if (agentConn) builtConnections = builtConnections.concat([agentConn]);
        setSnapshot();
        prevNodeId = agentId;
        xOffset += 350;
        var capturedAgentId = agentId;
        runStep(index, capturedAgentId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'classifier') {
        var clfTpl = null;
        for (var clfi = 0; clfi < objectTemplates.length; clfi++) {
          if (objectTemplates[clfi].id === 'sys-classifier') { clfTpl = objectTemplates[clfi]; break; }
        }
        if (!clfTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var clfId = 'sys-classifier-' + Date.now().toString(36) + '-' + index;
        var clfBase = cloneTemplateNode(clfTpl, clfId, { left: xOffset, top: yCenter });
        var clfNode = Object.assign({}, clfBase, {
          paramValues: { labels: step.labels || '', instruction: step.instruction || '' }
        });
        builtNodes = builtNodes.concat([clfNode]);
        registerPlannerStepNode(step, index, clfId);
        var clfConn = createPlannedConnection(step, clfId);
        if (clfConn) builtConnections = builtConnections.concat([clfConn]);
        setSnapshot();
        prevNodeId = clfId;
        xOffset += 350;
        var capturedClfId = clfId;
        runStep(index, capturedClfId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'router') {
        var rtrTpl = null;
        for (var rtri = 0; rtri < objectTemplates.length; rtri++) {
          if (objectTemplates[rtri].id === 'sys-router') { rtrTpl = objectTemplates[rtri]; break; }
        }
        if (!rtrTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var rtrId = 'sys-router-' + Date.now().toString(36) + '-' + index;
        var rtrBase = cloneTemplateNode(rtrTpl, rtrId, { left: xOffset, top: yCenter });
        var rtrNode = Object.assign({}, rtrBase, {
          paramValues: { labels: step.labels || '' }
        });
        builtNodes = builtNodes.concat([rtrNode]);
        registerPlannerStepNode(step, index, rtrId);
        var rtrConn = createPlannedConnection(step, rtrId);
        if (rtrConn) builtConnections = builtConnections.concat([rtrConn]);
        setSnapshot();
        prevNodeId = rtrId;
        xOffset += 350;
        var capturedRtrId = rtrId;
        runStep(index, capturedRtrId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'file-preview') {
        var fpTpl = null;
        for (var fpi = 0; fpi < objectTemplates.length; fpi++) {
          if (objectTemplates[fpi].id === 'sys-file-preview') { fpTpl = objectTemplates[fpi]; break; }
        }
        if (!fpTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var fpId = 'sys-file-preview-' + Date.now().toString(36) + '-' + index;
        var fpBase = cloneTemplateNode(fpTpl, fpId, { left: xOffset, top: yCenter });
        var fpNode = Object.assign({}, fpBase, {
          paramValues: { url: step.url || '' }
        });
        builtNodes = builtNodes.concat([fpNode]);
        registerPlannerStepNode(step, index, fpId);
        var fpConn = createPlannedConnection(step, fpId);
        if (fpConn) builtConnections = builtConnections.concat([fpConn]);
        setSnapshot();
        prevNodeId = fpId;
        xOffset += 350;
        var capturedFpId = fpId;
        runStep(index, capturedFpId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (step.type === 'computer-use') {
        var cuTpl = null;
        for (var cui = 0; cui < objectTemplates.length; cui++) {
          if (objectTemplates[cui].id === 'sys-computer-use') { cuTpl = objectTemplates[cui]; break; }
        }
        if (!cuTpl) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }
        var cuId = 'sys-computer-use-' + Date.now().toString(36) + '-' + index;
        var cuBase = cloneTemplateNode(cuTpl, cuId, { left: xOffset, top: yCenter });
        var cuNode = Object.assign({}, cuBase, {
          title: step.description ? step.description.substring(0, 40) : 'Computer Use',
          paramValues: {
            prompt: step.prompt || '',
            maxTokens: step.maxTokens || 4000
          }
        });
        builtNodes = builtNodes.concat([cuNode]);
        registerPlannerStepNode(step, index, cuId);
        var cuConn = createPlannedConnection(step, cuId);
        if (cuConn) builtConnections = builtConnections.concat([cuConn]);
        setSnapshot();
        prevNodeId = cuId;
        xOffset += 350;
        var capturedCuId = cuId;
        runStep(index, capturedCuId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
        return;
      }

      if (!step.prompt) { if (cb.onStepDone) cb.onStepDone(index); processStep(index + 1); return; }

      var sourceNodeId = resolvePlannerSourceNodeId(step);
      var sourceAnchor = resolvePlannerSourceAnchor(step, sourceNodeId);
      var ctx = buildGenerateContext(sourceNodeId);
      // The connection that WILL be created carries this propPath (see
      // line where `conn.propPath = String(step.inputPath).trim()`). Surface
      // it so codegen knows the exact value flowing into inputs[0] and
      // doesn't invent a duplicate hyperparameter for it.
      if (step.fromInput && step.inputPath) {
        ctx.incomingPropPath = String(step.inputPath).trim();
      }

      var codingCardPos = { left: xOffset - 110, top: yCenter - 24 };
      var fakeConnector = { nodeId: sourceNodeId || prevNodeId, anchor: sourceAnchor };
      setAiCodingState({ position: codingCardPos, futureConnector: fakeConnector });
      autoPanToCodeCard(codingCardPos);

      var customGeneratePromise;
      if (props.onGenerateWithAiPrompt) {
        customGeneratePromise = Promise.resolve(props.onGenerateWithAiPrompt({
          prompt: step.prompt,
          agentMode: !!step.agentMode,
          responseFields: step.responseFields || [],
          sourceNodeId: ctx.sourceNodeId,
          sourceAnchor: sourceAnchor,
          sourceNodeTitle: ctx.sourceNodeTitle,
          nodeFlow: ctx.nodeFlow,
          sourceOutput: ctx.sourceOutput,
          sourceVariables: ctx.sourceVariables
        }));
      } else {
        customGeneratePromise = request('/custom-objects/generate', {
          method: 'POST',
          body: {
            prompt: step.prompt,
            agentMode: !!step.agentMode,
            responseFields: step.responseFields || [],
            context: ctx
          }
        }).then(function(data) {
          return Object.assign({}, buildGeneratedCustomTemplate(data), { generationPrompt: step.prompt, changeHistory: [] });
        });
      }

      customGeneratePromise.then(function(template) {
        setAiCodingState(null);
        var newNodeId = template.id + '-node-' + Date.now().toString(36);
        var newNode = cloneTemplateNode(template, newNodeId, { left: xOffset, top: yCenter });

        builtNodes = builtNodes.concat([newNode]);
        registerPlannerStepNode(step, index, newNodeId);
        var customConn = createPlannedConnection(step, newNodeId);
        if (customConn) builtConnections = builtConnections.concat([customConn]);
        setSnapshot();
        prevNodeId = newNodeId;
        xOffset += 350;

        var capturedCustomId = newNodeId;
        runStep(index, capturedCustomId, function(output) {
          if (output !== undefined) prevNodeOutput = output;
          processStep(index + 1);
        });
      }).catch(function(err) {
        setAiCodingState(null);
        if (cb.onError) cb.onError(err);
      });
    }

    processStep(0);
  }

  return {
    autoBuildWorkflow: autoBuildWorkflow,
    handleConfirmStartQa: handleConfirmStartQa
  };
}

module.exports = useAutoBuildWorkflow;
